import {
  Flex,
  Skeleton,
  Table,
  Tbody,
  Th,
  Thead,
  Tr,
  Td,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import React, { useCallback, useEffect } from 'react';
import { useFormContext, useWatch, useFormState } from 'react-hook-form';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';

import { InputContainer, CurrencyInput } from '../../Inputs';
import {
  useEditLoanContext,
  EditLoanFormFields,
} from '../../useEditLoanContext';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from 'packages/common-frontend/utils';

type FundingRequestFormProps = {
  fundingRequestId: string;
};

const ErrorAlert = ({ message }: { message: any }) => {
  if (typeof message !== 'string') return null;
  return (
    <Alert status="error">
      <AlertIcon />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
};

export const FundingRequestEditForm = ({
  fundingRequestId,
}: FundingRequestFormProps) => {
  const context = useEditLoanContext();
  const {
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
  } = useFormContext();
  const formState = useFormState<EditLoanFormFields>();
  const fundingRequestComponents = useWatch({
    control,
    name: `fundingRequests.${fundingRequestId}.components`,
  });

  useEffect(() => {
    const globalRequestAmount = Number(
      getValues(`fundingRequests.${fundingRequestId}.amount`),
    );

    const componentsSum = Object.values<{
      amount: number;
    }>(fundingRequestComponents).reduce(
      (acc, { amount }) => acc + Number(amount),
      0,
    );

    if (globalRequestAmount !== componentsSum) {
      setError(`fundingRequests.${fundingRequestId}.components`, {
        message: `The total of ${formatCurrencyAmount(
          componentsSum,
          Bank.Currency.USD,
        )} does not match the draw request amount of ${formatCurrencyAmount(
          globalRequestAmount,
          Bank.Currency.USD,
        )}. Revise accordingly.`,
      });
    }
    if (globalRequestAmount === componentsSum) {
      clearErrors(`fundingRequests.${fundingRequestId}.components`);
    }
  }, [
    fundingRequestId,
    fundingRequestComponents,
    getValues,
    setError,
    clearErrors,
  ]);

  const handleGlobalDrawUpdate = useCallback(() => {
    const globalRequestAmount = Number(
      getValues(`fundingRequests.${fundingRequestId}.amount`),
    );

    if (context.status === 'ready') {
      const lenderShares =
        context.state.initial.resolvedLoan.funding.lenderShares;
      const loanFundingData = context.state.initial.resolvedLoan.funding.requests.find(
        (request) => request.fundingRequestId === fundingRequestId,
      );

      finleyAssert(
        loanFundingData,
        `Funding request [id: ${fundingRequestId}] not found`,
      );

      for (const {
        lenderShareId,
        fundingRequestComponentId,
      } of loanFundingData.fundingComponents) {
        const lenderShare = lenderShares.find(
          (currentLenderShare) =>
            currentLenderShare.lenderShareId === lenderShareId,
        );
        finleyAssert(
          lenderShare,
          `Lender share [id: ${lenderShareId}] not found`,
        );

        const lenderShareAmount =
          lenderShare.allocationPercent * globalRequestAmount;

        setValue(
          `fundingRequests.${fundingRequestId}.components.${fundingRequestComponentId}.amount`,
          lenderShareAmount,
        );
      }
    }
  }, [
    context.status,
    context.state?.initial.resolvedLoan.funding.lenderShares,
    context.state?.initial.resolvedLoan.funding.requests,
    fundingRequestId,
    getValues,
    setValue,
  ]);

  if (context.status !== 'ready') {
    // TODO: add loading state
    return <Skeleton w="100%" h="10rem" />;
  }

  const lenderShares = context.state.initial.resolvedLoan.funding.lenderShares;
  const loanFundingData = context.state.initial.resolvedLoan.funding.requests.find(
    (request) => request.fundingRequestId === fundingRequestId,
  );

  finleyAssert(
    loanFundingData,
    `Funding request [id: ${fundingRequestId}] not found`,
  );

  return (
    <Flex width="100%" gap="1rem" alignItems="flex-start" direction="column">
      <InputContainer label="Draw Request Amount">
        <CurrencyInput
          name={`fundingRequests.${fundingRequestId}.amount`}
          rules={{
            valueAsNumber: true,
            onBlur: handleGlobalDrawUpdate,
          }}
        />
      </InputContainer>
      <Table>
        <Thead>
          <Tr>
            <Th>Lender</Th>
            <Th>Share</Th>
            <Th>Outstanding Balance</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loanFundingData.fundingComponents.map(
            ({ fundingRequestComponentId, lenderShareId }) => {
              const lenderShare = lenderShares.find(
                (currentLenderShare) =>
                  currentLenderShare.lenderShareId === lenderShareId,
              );
              finleyAssert(
                lenderShare,
                `Lender share [id: ${lenderShareId}] not found`,
              );

              return (
                <Tr key={fundingRequestComponentId}>
                  <Td>{lenderShare.lenderOrganization.name}</Td>
                  <Td>
                    {percentageToPercentageLabel(
                      commonUtils.divideByOrZero(
                        fundingRequestComponents[fundingRequestComponentId]
                          .amount,
                        getValues(`fundingRequests.${fundingRequestId}.amount`),
                      ) * 100,
                      2,
                      true,
                    )}
                  </Td>
                  <Td>
                    <CurrencyInput
                      name={`fundingRequests.${fundingRequestId}.components.${fundingRequestComponentId}.amount`}
                      showErrorMessage={false}
                      rules={{
                        validate: {
                          invalidComponentsSum: (_, { fundingRequests }) => {
                            const fundingRequestValues =
                              fundingRequests[fundingRequestId];

                            const totalRequestedAmount =
                              fundingRequestValues.amount;

                            const componentsSum = Object.values<{
                              amount: number;
                            }>(fundingRequestValues.components).reduce(
                              (acc, { amount }) => acc + Number(amount),
                              0,
                            );

                            return (
                              Number(totalRequestedAmount) ===
                                Number(componentsSum) || ''
                            );
                          },
                        },
                        deps: [
                          //   `fundingRequests.${fundingRequestId}.amount`,
                          `fundingRequests.${fundingRequestId}.components`,
                        ],
                      }}
                    />
                  </Td>
                </Tr>
              );
            },
          )}
        </Tbody>
      </Table>
      <ErrorAlert
        message={
          formState.errors?.fundingRequests?.[fundingRequestId]?.components
            ?.message
        }
      />
    </Flex>
  );
};
